import { useCallback, useEffect, useMemo, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { MIN_CORRECTION_REASON, errorInfo } from "../lib/collaborators";
import { LEVEL_CODES, SOURCE_LABELS, expectedSkills, skillRows } from "../lib/skills";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Loader2, Pencil, Plus } from "lucide-react";


// Fonctions métier et compétences d'une fiche. Les fonctions disent ce que fait la personne, les compétences ce qu'elle sait faire ;
// ni l'une ni l'autre ne donne d'accès au CRM. Saisir un niveau demande « skills:assess » ; jamais sur sa propre fiche (sauf administrateur).
export default function CollaboratorSkillsCard({ fiche, refs, canAssess, isAdmin = false, onHistoryChange }) {
  const [entries, setEntries] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [editing, setEditing] = useState(null); // null | { skill_id?, entry?, expected? }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/collaborators/${fiche.id}/skills`);
      setEntries(data.items);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  }, [fiche.id]);

  useEffect(() => {
    load();
  }, [load]);

  const expected = useMemo(() => expectedSkills(refs.functions, fiche.job_function_ids), [refs.functions, fiche.job_function_ids]);
  const rows = useMemo(() => skillRows(entries, expected).sort((a, b) => refs.skillLabel(a.skill_id).localeCompare(refs.skillLabel(b.skill_id), "fr")),
    [entries, expected, refs]);
  // Someone who left is no longer evaluated: only an administrator corrects the past, with a reason, and never on their own fiche (the server enforces it).
  const exited = fiche.status === "exited";
  const correction = exited && isAdmin && canAssess && !fiche.is_me && !fiche.archived;
  const assessable = (canAssess && !fiche.archived && !exited) || correction;
  const unrecorded = refs.skills.filter((s) => s.active && !entries.some((e) => e.skill_id === s.id));

  return (
    <Card className="p-5 space-y-3" data-testid="collab-skills">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display font-semibold text-sm text-slate-900"><GraduationCap size={15} /> Fonctions et compétences</div>
        {assessable && unrecorded.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setEditing({})} data-testid="btn-skill-record">
            <Plus size={14} className="mr-1.5" /> {correction ? "Corriger l'historique" : "Ajouter un niveau"}
          </Button>
        )}
      </div>
      {exited && !fiche.archived && (
        <p className="text-xs text-slate-500" data-testid="collab-skills-exited">
          Cette personne est sortie : aucune nouvelle évaluation.{correction ? " En tant qu'administrateur, vous pouvez corriger l'historique, avec un motif." : ""}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5" data-testid="collab-functions">
        {(fiche.job_function_ids || []).length === 0
          ? <span className="text-sm text-slate-500">Aucune fonction métier.</span>
          : fiche.job_function_ids.map((id) => (
            <span key={id} className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700" data-testid="collab-function-chip">{refs.functionLabel(id)}</span>
          ))}
      </div>

      {state === "loading" && <Skeleton className="h-16 w-full" />}
      {state === "error" && (
        <div className="text-sm text-slate-600 space-y-2">
          <p>Impossible de charger les compétences.</p>
          <Button variant="outline" size="sm" onClick={load}>Réessayer</Button>
        </div>
      )}
      {state === "ready" && rows.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="collab-skills-empty">Aucune compétence enregistrée{assessable ? " : ajoutez un premier niveau." : "."}</p>
      )}
      {state === "ready" && rows.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="collab-skills-list">
          {rows.map((row) => (
            <li key={row.skill_id} className="flex items-start justify-between gap-3 py-2" data-testid={`collab-skill-${row.skill_id}`}>
              <div className="min-w-0">
                <div className="text-sm text-slate-900 break-words">{row.entry?.skill_label || refs.skillLabel(row.skill_id)}</div>
                <div className="text-xs text-slate-500">
                  {row.entry ? `${refs.levelLabel(row.entry.level)} · ${SOURCE_LABELS[row.entry.source]} par ${row.entry.recorded_by_name}, le ${new Date(row.entry.recorded_at).toLocaleDateString("fr-FR")}` : "Pas encore évaluée"}
                  {row.expected && <> · attendu : {refs.levelLabel(row.expected.level)}</>}
                </div>
                {row.gap && <div className="text-xs text-amber-700" data-testid="collab-skill-gap">En dessous du niveau attendu par {row.expected.functions.join(", ")}</div>}
              </div>
              {assessable && (
                <Button variant="ghost" size="icon" aria-label={`Modifier le niveau : ${refs.skillLabel(row.skill_id)}`} onClick={() => setEditing({ skill_id: row.skill_id, entry: row.entry })}
                  disabled={!row.entry && !refs.skills.find((s) => s.id === row.skill_id)?.active} data-testid={`btn-skill-edit-${row.skill_id}`}>
                  <Pencil size={15} />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <LevelDialog
        open={!!editing} onOpenChange={(v) => !v && setEditing(null)} fiche={fiche} refs={refs} target={editing} choices={unrecorded} correction={correction}
        onDone={async () => { setEditing(null); await load(); onHistoryChange?.(); }}
        onConflict={async () => { setEditing(null); await load(); }}
      />
    </Card>
  );
}

function LevelDialog({ open, onOpenChange, fiche, refs, target, choices, correction, onDone, onConflict }) {
  const [skillId, setSkillId] = useState("");
  const [level, setLevel] = useState("autonomous");
  const [source, setSource] = useState("assessed");
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const entry = target?.entry || null;

  useEffect(() => {
    if (!open) return;
    setSkillId(target?.skill_id || "");
    setLevel(entry?.level || "autonomous");
    setSource(entry?.source || "assessed");
    setReason("");
  }, [open, target, entry]);

  const unchanged = !!entry && entry.level === level && entry.source === source;
  const reasonOk = !correction || reason.trim().length >= MIN_CORRECTION_REASON;
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/collaborators/${fiche.id}/skills/${skillId}`, { expected_version: entry?.version ?? null, level, source, ...(correction ? { correction_reason: reason.trim() } : {}) });
      toast.success("Niveau enregistré");
      await onDone();
    } catch (error) {
      const info = errorInfo(error);
      if (info.code === "version_conflict") {
        toast.error(info.data.msg);
        await onConflict();
      } else {
        toast.error(info.data?.msg || errorMessage(error));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="skill-level-dialog">
        <DialogHeader>
          <DialogTitle>{target?.skill_id ? refs.skillLabel(target.skill_id) : correction ? "Corriger l'historique d'une compétence" : "Ajouter un niveau de compétence"}</DialogTitle>
          <DialogDescription>{correction ? `Correction historique pour ${fiche.display_name}, qui est sorti(e). Le motif est conservé dans l'historique de la fiche.` : `Niveau actuel de ${fiche.display_name}. Chaque changement est conservé dans l'historique de la fiche.`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!target?.skill_id && (
            <div className="space-y-2">
              <Label>Compétence *</Label>
              <Select value={skillId || undefined} onValueChange={setSkillId}>
                <SelectTrigger data-testid="select-skill"><SelectValue placeholder="Choisir une compétence" /></SelectTrigger>
                <SelectContent>{choices.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Niveau *</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger data-testid="select-skill-level"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVEL_CODES.map((code) => <SelectItem key={code} value={code}>{refs.levelLabel(code)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Source *</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="select-skill-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="assessed">Évalué (observé par vous)</SelectItem>
                  <SelectItem value="declared">Déclaré (par la personne)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {correction && (
            <div className="space-y-2">
              <Label htmlFor="sk-reason">Motif de la correction *</Label>
              <Textarea id="sk-reason" rows={2} maxLength={200} className="text-base md:text-sm" data-testid="input-skill-correction-reason" value={reason}
                placeholder="Ex. : niveau saisi par erreur avant le départ" onChange={(e) => setReason(e.target.value)} />
              <p className="text-xs text-slate-500">{MIN_CORRECTION_REASON} caractères au moins.</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!skillId || unchanged || !reasonOk || busy}  data-testid="btn-skill-level-save">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
